/* PipelineRow (DESIGN14 §F1.5, DESIGN24 §T1.5) — the compact four-stage
   pipeline strip for the mini app: Architecture · Estimate · Pricing ·
   Proposal. Each stage is a dot + short label; ✓ done, ● in review / drafted,
   ○ not started, and an animated running dot while that module has a live run.
   Statuses ONLY — never content — so it is safe for every role, exactly like
   the web PipelineStrip it mirrors. Reads the shared Pipeline shape from the
   architect API client (single source of the stage vocabulary).

   DESIGN23 flipped Pricing IN FRONT OF Proposal on every surface, because a
   proposal is now approved with its final numbers (root prices it first, and
   the proposal approve is refused until then). Only the DISPLAY order moved —
   the Pipeline fields are unchanged and the backend derives them exactly as
   before. */

import React from 'react';
import { Pipeline } from '../../architect/api';

type StepState = 'done' | 'progress' | 'todo';

const GLYPH: Record<StepState, string> = { done: '✓', progress: '●', todo: '○' };

function archState(s: Pipeline['architecture'] | undefined): StepState {
  if (s === 'approved') return 'done';
  if (s === 'draft') return 'progress';
  return 'todo';
}
function reviewState(s: 'none' | 'review' | 'approved' | undefined): StepState {
  if (s === 'approved') return 'done';
  if (s === 'review') return 'progress';
  return 'todo';
}

export interface PipelineRowProps {
  pipeline?: Pipeline | null;
  /** 'full' shows labels (project header); 'compact' hides them (list cards). */
  variant?: 'full' | 'compact';
  className?: string;
}

export function PipelineRow({ pipeline, variant = 'full', className }: PipelineRowProps) {
  const p = pipeline || undefined;
  const running = p?.running;
  const pricing = (p as Pipeline & { pricing?: 'none' | 'draft' | 'approved' } | undefined)?.pricing;
  const steps: Array<{ key: string; label: string; state: StepState; running: boolean }> = [
    {
      key: 'architecture',
      label: 'Architecture',
      state: archState(p?.architecture),
      running: !!running?.architecture,
    },
    {
      key: 'estimate',
      label: 'Estimate',
      state: reviewState(p?.estimate),
      running: !!running?.estimate,
    },
    {
      key: 'priced',
      label: 'Pricing',
      state: pricing === 'approved' || (!pricing && p?.priced) ? 'done' : pricing === 'draft' ? 'progress' : 'todo',
      running: false,
    },
    {
      key: 'proposal',
      label: 'Proposal',
      state: reviewState(p?.proposal),
      running: !!running?.proposal,
    },
  ];
  return (
    <div
      className={
        'tma-pipe tma-pipe-' + variant + (className ? ' ' + className : '')
      }
      role="list"
      aria-label="Project stages"
    >
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 ? (
            <span className={'tma-pipe-conn ' + steps[i - 1].state} aria-hidden="true" />
          ) : null}
          <span
            className={'tma-pipe-step ' + s.state + (s.running ? ' running' : '')}
            role="listitem"
            aria-label={`${s.label}: ${s.state === 'done' ? 'approved' : s.running ? 'generating' : s.state === 'progress' ? 'in review' : 'not started'}`}
          >
            <span className="tma-pipe-dot" aria-hidden="true">
              {s.running ? <span className="tma-spin tma-pipe-spin" /> : GLYPH[s.state]}
            </span>
            {variant === 'full' ? (
              <span className="tma-pipe-label">{s.label}</span>
            ) : null}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

export default PipelineRow;
